import { motion } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';

import { Box, Flex } from '@bonusly/design-system';

import { BizySettingsModal } from '../../modules/bizy-settings/bizy-settings';
import { BizySidePanelChatView } from './bizy-side-panel-chat-view';
import { BizySidePanelHistoryDrawer } from './bizy-side-panel-history-drawer';
import { BizySidePanelHistoryView } from './bizy-side-panel-history-view';
import { BizySidePanelResizeHandle } from './bizy-side-panel-resize-handle';
import { useBizyOpenListener } from './hooks/use-bizy-open-listener';
import { BIZY_FULLSCREEN_DRAWER_MAX_WIDTH, BIZY_FULLSCREEN_DRAWER_MIN_WIDTH, useBizySidePanelStore } from './store/use-bizy-side-panel-store';

export type BizyPanelGradientTheme = 'none' | 'cool-subtle';

interface BizySidePanelProps {
	gradientTheme?: BizyPanelGradientTheme;
	isMobileMode?: boolean;
	panelLeftEdge: number;
	showFullscreenToggle?: boolean;
}

export function BizySidePanel({ gradientTheme = 'none', isMobileMode = false, panelLeftEdge, showFullscreenToggle = true }: BizySidePanelProps) {
	const isOpen = useBizySidePanelStore((state) => state.isOpen);
	const isFullscreen = useBizySidePanelStore((state) => state.isFullscreen);
	const view = useBizySidePanelStore((state) => state.view);
	const showChat = useBizySidePanelStore((state) => state.showChat);
	const showHistory = useBizySidePanelStore((state) => state.showHistory);
	const fullscreenDrawerWidth = useBizySidePanelStore((state) => state.fullscreenDrawerWidth);
	const isHistoryDrawerOpen = useBizySidePanelStore((state) => state.isHistoryDrawerOpen);
	const isSettingsModalOpen = useBizySidePanelStore((state) => state.isSettingsModalOpen);
	const settingsSection = useBizySidePanelStore((state) => state.settingsSection);
	const closeHistoryDrawer = useBizySidePanelStore((state) => state.closeHistoryDrawer);
	const closeSettingsModal = useBizySidePanelStore((state) => state.closeSettingsModal);
	const setFullscreenDrawerWidth = useBizySidePanelStore((state) => state.setFullscreenDrawerWidth);
	const [resetSignal, setResetSignal] = useState(0);
	const [activeConversationId, setActiveConversationId] = useState<string | undefined>();

	useBizyOpenListener();

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		if (e.key === 'Escape' && !e.defaultPrevented) {
			const store = useBizySidePanelStore.getState();
			if (store.isSettingsModalOpen) {
				store.closeSettingsModal();
			} else if (store.isHistoryDrawerOpen) {
				store.closeHistoryDrawer();
			} else if (store.isFullscreen) {
				store.exitFullscreen();
			} else {
				store.close('keyboard');
			}
		}
	}, []);

	const PANEL_BG = 'linear-gradient(173deg, rgba(107, 116, 133, 0.02) 0%, rgba(107, 116, 133, 0.06) 100%)';
	const EASING = [0.4, 0, 0.2, 1] as const;
	const transition = { duration: 0.35, ease: EASING };
	const compactSlideTransition = 'transform 0.25s ease-in-out, opacity 0.25s ease-in-out';

	const historyBg = useMemo(() => {
		if (gradientTheme === 'none') return PANEL_BG;
		return 'linear-gradient(180deg, var(--chakra-colors-bg-canvas) 0%, var(--chakra-colors-bg-surface) 100%)';
	}, [gradientTheme]);

	const panelBg = view === 'history' ? historyBg : PANEL_BG;
	const isFullscreenLayout = isFullscreen && !isMobileMode;
	const canShowFullscreenToggle = showFullscreenToggle && !isMobileMode;

	return (
		<Flex
			data-component="bizy-side-panel"
			direction="column"
			h="100%"
			bgColor="bg.surface"
			bgImage={isFullscreenLayout ? 'none' : panelBg}
			borderLeft="1px solid"
			borderColor="border.soft"
			position="relative"
			overflow="hidden"
			overscrollBehavior="contain"
			onKeyDown={handleKeyDown}
			{...(!isOpen && { inert: '' })}
		>
			{view === 'history' && (
				<BizySidePanelHistoryView
					onBack={showChat}
					onNewConversation={() => {
						setResetSignal((value) => value + 1);
						setActiveConversationId(undefined);
						showChat();
					}}
					onResetChat={() => {
						setResetSignal((value) => value + 1);
						setActiveConversationId(undefined);
					}}
				/>
			)}
			<Flex direction={isFullscreenLayout ? 'row' : 'column'} h="100%" minH={0} display={view === 'chat' ? 'flex' : 'none'}>
				{isFullscreenLayout ? (
					<>
						<motion.div
							initial={false}
							animate={{ width: isHistoryDrawerOpen ? fullscreenDrawerWidth : 0, opacity: isHistoryDrawerOpen ? 1 : 0 }}
							transition={transition}
							style={{ height: '100%', overflow: 'hidden', flexShrink: 0, position: 'relative' }}
						>
							{isHistoryDrawerOpen && (
								<>
									<BizySidePanelHistoryDrawer
										activeConversationId={activeConversationId}
										isMobileMode={isMobileMode}
										onNewConversation={() => {
											setResetSignal((value) => value + 1);
											setActiveConversationId(undefined);
											showChat();
										}}
										onResetChat={() => {
											setResetSignal((value) => value + 1);
											setActiveConversationId(undefined);
										}}
										showFullscreenToggle={canShowFullscreenToggle}
									/>
									<BizySidePanelResizeHandle
										panelLeftEdge={panelLeftEdge}
										onResizeWidth={setFullscreenDrawerWidth}
										minWidth={BIZY_FULLSCREEN_DRAWER_MIN_WIDTH}
										maxWidth={BIZY_FULLSCREEN_DRAWER_MAX_WIDTH}
									/>
								</>
							)}
						</motion.div>
						<Flex direction="column" h="100%" minW={0} flex={1}>
							<BizySidePanelChatView
								onConversationIdChange={setActiveConversationId}
								onOpenHistory={showHistory}
								resetSignal={resetSignal}
								showFullscreenToggle={canShowFullscreenToggle}
							/>
						</Flex>
					</>
				) : (
					<Box position="relative" h="100%" minH={0} w="100%" overflow="hidden">
						<Box
							position={isHistoryDrawerOpen ? 'absolute' : 'relative'}
							top={0}
							left={0}
							right={0}
							h="100%"
							minH={0}
							transform={isHistoryDrawerOpen ? 'translateX(100%)' : 'translateX(0)'}
							opacity={isHistoryDrawerOpen ? 0 : 1}
							visibility={isHistoryDrawerOpen ? 'hidden' : 'visible'}
							transition={compactSlideTransition}
							pointerEvents={isHistoryDrawerOpen ? 'none' : 'auto'}
							{...(isHistoryDrawerOpen && { inert: '' })}
						>
							<BizySidePanelChatView
								onConversationIdChange={setActiveConversationId}
								onOpenHistory={showHistory}
								resetSignal={resetSignal}
								showFullscreenToggle={canShowFullscreenToggle}
							/>
						</Box>
						<Box
							position={isHistoryDrawerOpen ? 'relative' : 'absolute'}
							top={0}
							left={0}
							right={0}
							h="100%"
							minH={0}
							transform={isHistoryDrawerOpen ? 'translateX(0)' : 'translateX(-100%)'}
							opacity={isHistoryDrawerOpen ? 1 : 0}
							visibility={isHistoryDrawerOpen ? 'visible' : 'hidden'}
							transition={compactSlideTransition}
							pointerEvents={isHistoryDrawerOpen ? 'auto' : 'none'}
							{...(!isHistoryDrawerOpen && { inert: '' })}
						>
							<BizySidePanelHistoryDrawer
								activeConversationId={activeConversationId}
								isMobileMode={isMobileMode}
								onNewConversation={() => {
									setResetSignal((value) => value + 1);
									setActiveConversationId(undefined);
									showChat();
									closeHistoryDrawer();
								}}
								onResetChat={() => {
									setResetSignal((value) => value + 1);
									setActiveConversationId(undefined);
								}}
								showFullscreenToggle={canShowFullscreenToggle}
							/>
						</Box>
					</Box>
				)}
			</Flex>
			<BizySettingsModal isOpen={isSettingsModalOpen} onClose={closeSettingsModal} section={settingsSection} />

			{!isFullscreenLayout && !isMobileMode && <BizySidePanelResizeHandle panelLeftEdge={panelLeftEdge} />}
		</Flex>
	);
}
